/* eslint-disable no-nested-ternary */

import React, { useCallback, useMemo } from 'react';
import { Button, Form, SelectOptionWithGroup } from 'tea-component';
import { getLocale } from 'umi';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import {
  CUSTOM_VALUE,
} from './config';
import './index.scss';
import '@/pages/dashboard/assets/scss/page/components/selectCompoents.scss';
import { IEventFilterContentProps } from './type';
import type { TEventParameterValue } from '@/pages/datalab/pages/ABExperiment/components/EventFilter/types';

import EventNameSelect from './components/EventNameSelect';
import EventParameterPanel from '@/pages/datalab/pages/ABExperiment/components/EventFilter/components/EventParameterPanel';
import HoursSinceRegisterSelect from './components/HoursSinceRegisterSelect';
import EventOccurCountInput from './components/EventOccurCountInput';
import { generateEventFilterTitle } from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/utils';
import { useEventExplorationFilter } from '@/pages/dashboard/filterPageComponents/eventAnalysis/EventExplorationFilter/hooks';
import classNames from 'classnames';

const DEFAULT_PARAMETER_VALUE: TEventParameterValue = {
  type: 'market',
  regions: [],
  markets: [],
};

const EventFilterContent: React.FC<IEventFilterContentProps> = (props) => {
  const {
    value,
    onChange,
    onConfirm,
    onCancel,
    eventNameOptions,
    eventParameterOptions,
    hoursSinceRegisterOptions = [],
    data,
    config,
  } = props;

  const { getLocalText, isEN } = useLocal('filter');
  const locale = getLocale();

  const {
    compEventNameSelect,
  } = config || {};

  const {
    getEventNameOptions,
    getEventParametersOptions,
  } = useEventExplorationFilter({
    isEN,
    data,
    eventFilterValue: value,
  });

  const handleEventNameChange = useCallback((val: string) => {
    onChange(Object.assign({}, value, { eventName: val, eventParameter: DEFAULT_PARAMETER_VALUE }));
  }, [value, onChange]);

  const handleEventParameterChange = useCallback((newValue: TEventParameterValue | null) => {
    onChange({ ...value, eventParameter: newValue || DEFAULT_PARAMETER_VALUE });
  }, [value, onChange]);

  const handleHoursSinceRegisterChange = useCallback((val: string) => {
    if (val === CUSTOM_VALUE) {
      onChange({ ...value, hoursSinceRegister: val });
    } else {
      onChange({ ...value, hoursSinceRegister: val, hoursSinceRegisterCustom: '' });
    }
  }, [value, onChange]);

  const handleHoursSinceRegisterCustomChange = useCallback((val: string) => {
    onChange({ ...value, hoursSinceRegisterCustom: val, hoursSinceRegister: CUSTOM_VALUE });
  }, [value, onChange]);

  const handleMinValueChange = useCallback((val: string) => {
    onChange({ ...value, minValue: val });
  }, [value, onChange]);

  const handleMaxValueChange = useCallback((val: string) => {
    const updated = { ...value, maxValue: val };
    if (val && !value.minValue) {
      updated.minValue = '0';
    }
    onChange(updated);
  }, [value, onChange]);

  const hasOccurCountError = useMemo(() => {
    const minVal = parseFloat(value.minValue || '0');
    const maxVal = parseFloat(value.maxValue || '');

    const isMinInteger = !value.minValue || Number.isInteger(Number(value.minValue));
    const isMaxInteger = !value.maxValue || Number.isInteger(Number(value.maxValue));

    if (!isMinInteger || !isMaxInteger) return true;

    if (value.maxValue && !isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal) {
      return true;
    }
    return false;
  }, [value.minValue, value.maxValue]);

  const hasHoursMaxExceeded = useMemo(() => {
    if (value.hoursSinceRegister !== CUSTOM_VALUE || !value.hoursSinceRegisterCustom) return false;
    const customHours = Number(value.hoursSinceRegisterCustom);
    return Number.isFinite(customHours) && customHours > 168;
  }, [value.hoursSinceRegister, value.hoursSinceRegisterCustom]);

  const isFormInvalid = useMemo(() => {
    const isEventNameEmpty = !value.eventName;
    const isHoursEmpty = !value.hoursSinceRegister || (value.hoursSinceRegister === CUSTOM_VALUE && !value.hoursSinceRegisterCustom);
    const isOccurCountEmpty = !value.maxValue;

    return isEventNameEmpty || isHoursEmpty || isOccurCountEmpty || hasOccurCountError || hasHoursMaxExceeded;
  }, [value, hasOccurCountError, hasHoursMaxExceeded]);

  const handleConfirm = useCallback(() => {
    if (isFormInvalid) return;
    const finalValue = { ...value };
    let hasChanged = false;

    if (!finalValue.minValue && finalValue.maxValue) {
      finalValue.minValue = '0';
      hasChanged = true;
    }

    const minVal = parseFloat(finalValue.minValue || '0');
    const maxVal = parseFloat(finalValue.maxValue || '');

    if (finalValue.maxValue && !isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal) {
      return;
    }

    if (hasChanged) {
      onChange(finalValue);
    }
    if (onConfirm)
      onConfirm(finalValue);
  }, [value, onChange, onConfirm, isFormInvalid]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const currentTitle = useMemo(() => {
    return generateEventFilterTitle({
      value,
      eventNameOptions: getEventNameOptions,
      eventParameterOptions: getEventParametersOptions,
      getLocalText,
      isEn: isEN,
    });
  }, [value, getEventNameOptions, getEventParametersOptions, getLocalText, isEN]);

  return (
    <div className="dbd-event-filter-content">
      <div className={`dbd-event-filter-content__title${currentTitle === getLocalText('please_select') ? ' dbd-event-filter-content__title--placeholder' : ''}`}>{currentTitle}</div>
      <Form className={classNames('dbd-event-filter-content__form', {
        'dbd-event-filter-content__form--en': isEN,
      })}>
        <Form.Item
          label={getLocalText('event_name')}
          required
        >
          <EventNameSelect
            value={value.eventName || ''}
            onChange={handleEventNameChange}
            groups={{
              eventName: getLocalText('event_name'),
            }}
            options={getEventNameOptions}
            placeholder={getLocalText('please_select')}
            selectProps={{
              listHeight: 180,
              ...compEventNameSelect?.selectProps,
              overlayClassName: 'dbd-event-filter-content__name-select__overlay',
              searchable: true,
              filter: (inputValue: string, option: SelectOptionWithGroup) => {
                const keyword = String(inputValue ?? '').toLowerCase().trim();
                if (!keyword) return true;
                const opt = option as SelectOptionWithGroup & { textEn?: string; textCN?: string; };
                const text = String(opt?.text ?? '').toLowerCase();
                const textEn = String(opt?.textEn ?? '').toLowerCase();
                const textCN = String(opt?.textCN ?? '').toLowerCase();
                const val = String(opt?.value ?? '').toLowerCase();
                return text.includes(keyword) || textEn.includes(keyword) || textCN.includes(keyword) || val.includes(keyword);
              },
            }}
          />
        </Form.Item>
        <Form.Item
          label={getLocalText('event_parameter')}
        >
          <EventParameterPanel
            value={value.eventParameter || DEFAULT_PARAMETER_VALUE}
            onChange={handleEventParameterChange}
            options={getEventParametersOptions}
            locale={locale}
            language={{
              name: getLocalText('event_parameter'),
              lftName: getLocalText('event_parameter'),
              rtName: getLocalText('please_select_value'),
            }}
            placeholder={getLocalText('please_select')}
            firstLevelTitle={getLocalText('overall')}
          />
        </Form.Item>
        <Form.Item
          label={getLocalText('hours_since_register')}
          required
        >
          <HoursSinceRegisterSelect
            value={value.hoursSinceRegister || ''}
            customValue={value.hoursSinceRegisterCustom || ''}
            onChange={handleHoursSinceRegisterChange}
            onCustomChange={handleHoursSinceRegisterCustomChange}
            options={hoursSinceRegisterOptions}
            placeholder={getLocalText('please_select_value')}
          />
        </Form.Item>
        <Form.Item
          label={getLocalText('event_occur_count')}
          required
        >
          <EventOccurCountInput
            minValue={value.minValue || ''}
            maxValue={value.maxValue || ''}
            onMinChange={handleMinValueChange}
            onMaxChange={handleMaxValueChange}
            placeholder={getLocalText('numbers')}
          />
        </Form.Item>
      </Form>
      <div className="dbd-event-filter-content__footer">
        <Button type="primary" onClick={handleConfirm} disabled={isFormInvalid}>{getLocalText('confirm')}</Button>
        <Button type="weak" onClick={handleCancel}>{getLocalText('cancel')}</Button>
      </div>
    </div>
  );
};

export default EventFilterContent;
